import React from "react";
import { PaymentForm } from "sawfish-payment-react-widget";
import { useCustomerPaymentInfo } from "../index";
import { sawfishConfig } from "../../../config";
import {
  Alert as MuiAlert,
  AlertTitle,
  CardContent,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useSubmitResultKeySawfish } from "../../../api/Api";
import { Payment } from "./types";
import {
  OneSolomonAlert,
  OneSolomonPaymentPage,
  OneSolomonRecaptchaNote,
  OneSolomonSawfishRoot,
  resolveIsCompanyOneSolomons,
} from "../oneSolomonUi";

const Alert = styled(MuiAlert)(spacing);

const SawfishPaymentForm = () => {
  const navigate = useNavigate();
  const { values: customerInfo } = useCustomerPaymentInfo();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const submitResultMutation = useSubmitResultKeySawfish();
  const isCompanyOneSolomons = resolveIsCompanyOneSolomons(
    customerInfo?.company_uuid,
    customerInfo?.isCompanyOneSolomons
  );

  const environment = customerInfo?.testMode
    ? "staging"
    : sawfishConfig.environment;
  const endpoint = customerInfo?.testMode
    ? "https://api-staging.sawfish.com.au"
    : sawfishConfig.endpoint;

  const handlePaymentSuccess = async (response: Payment) => {
    if (!executeRecaptcha) {
      console.error(
        "handlePaymentSuccess() was called even though executeRecaptcha is not yet available"
      );
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha(
        "SawfishPaymentForm_handlePaymentSuccess"
      );

      await submitResultMutation.mutateAsync({
        "g-recaptcha-response": recaptchaToken,
        transaction_for: customerInfo?.transaction_for || "",
        transaction_uuid: customerInfo?.transaction_uuid || "",
        sawfish_transaction_uuid: response.uuid || "",
        merchant_reference: customerInfo?.merchantReference,
        order_uuid: customerInfo?.order_uuid,
        name: `${customerInfo?.firstName || ""} ${
          customerInfo?.lastName || ""
        }`.trim(),
        email: customerInfo?.email,
        totalQuoteAmount: customerInfo?.totalQuoteAmount?.toString(),
        amount: customerInfo?.amount,
      });

      navigate("/payment-result", {
        state: {
          ...response,
          transaction_for: customerInfo?.transaction_for,
          company_image: customerInfo?.company_image,
          company_uuid: customerInfo?.company_uuid,
          isCompanyOneSolomons: customerInfo?.isCompanyOneSolomons,
        },
      });
    } catch (error) {
      console.error("Failed to submit result to backend:", error);
    }
  };

  const handlePaymentError = (error: any) => {
    navigate("/payment-result", {
      state: {
        uuid: error?.uuid || "",
        status: error?.status || "payment_failed",
        transaction_for: customerInfo?.transaction_for,
        company_image: customerInfo?.company_image,
        company_uuid: customerInfo?.company_uuid,
        isCompanyOneSolomons: customerInfo?.isCompanyOneSolomons,
      } as Partial<Payment> & {
        transaction_for?: string;
        company_image?: string;
        company_uuid?: string;
        isCompanyOneSolomons?: boolean;
      },
    });
  };

  const testModeAlert = Boolean(customerInfo?.testMode) && (
    isCompanyOneSolomons ? (
      <OneSolomonAlert variant="warning">
        <strong>TEST MODE</strong>
        {customerInfo?.testModeReason ? `: ${customerInfo.testModeReason}` : ""}
        <br />
        Payments will be marked as TEST mode.
      </OneSolomonAlert>
    ) : (
      <Alert severity="warning" my={4}>
        <AlertTitle>
          TEST MODE{" "}
          {!!customerInfo?.testModeReason &&
            `: ${customerInfo?.testModeReason}`}
        </AlertTitle>
        Payments will be marked as TEST mode.
      </Alert>
    )
  );

  const sawfishWidget = !!customerInfo && (
    <OneSolomonSawfishRoot>
      <PaymentForm
        environment={environment}
        endpoint={endpoint}
        apiSecret={sawfishConfig.apiSecret}
        clientId={sawfishConfig.clientId}
        amount={customerInfo?.amount || customerInfo?.totalQuoteAmount || 0}
        contact={{
          first_name: customerInfo?.firstName,
          last_name: customerInfo?.lastName,
          email: customerInfo?.email,
          country: "Australia",
        }}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        metadata={customerInfo}
        title={isCompanyOneSolomons ? undefined : "Enter card details"}
        description={customerInfo?.merchantReference}
      />
    </OneSolomonSawfishRoot>
  );

  if (isCompanyOneSolomons) {
    return (
      <OneSolomonPaymentPage title="Credit Card Payment">
        {testModeAlert}
        {sawfishWidget}
        <OneSolomonRecaptchaNote>
          Protected by reCAPTCHA.{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy
          </a>{" "}
          &middot;{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>
        </OneSolomonRecaptchaNote>
      </OneSolomonPaymentPage>
    );
  }

  return (
    <CardContent>
      {testModeAlert}
      {sawfishWidget}
    </CardContent>
  );
};

export default SawfishPaymentForm;
